import React, { useReducer } from 'react';
import clsx from 'clsx';
import { Theme } from '@radix-ui/themes';
import { ThemeProvider } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import { useKeyboardShortcut } from 'utils/hooks';
import { LS_KEYS, setLocalItem, getLocalItem } from 'utils/localStorage';
import GlobalStyle from 'components/styled/GlobalStyle';

import lightTheme from './lightTheme';
import darkTheme from './darkTheme';

export type ThemeName = 'light' | 'dark';

type ThemeStateValue = {
  themeName: ThemeName;
  changeTheme: (themeName: ThemeName) => void;
};

type ThemeAction = {
  type: 'THEME_CHANGE';
  payload: ThemeName;
};

type ThemeStateType = {
  themeName: ThemeName;
};

type ThemeStateProviderProps = {
  forceTheme?: ThemeName;
  children: React.ReactNode;
};

const getStoredThemeName = (): ThemeName => {
  const storedThemeName = getLocalItem(LS_KEYS.theme.key);
  return storedThemeName?.toLowerCase() === 'dark' ? 'dark' : 'light';
};

const initialState: ThemeStateValue = {
  themeName: getStoredThemeName(),
  changeTheme: () => {},
};

export const ThemeState = React.createContext<ThemeStateValue>(initialState);

export const getTheme = (themeName?: string | null): DefaultTheme => {
  return themeName?.toLowerCase() === 'light' ? lightTheme : darkTheme;
};

const reducer = (state: ThemeStateType, action: ThemeAction): ThemeStateType => {
  switch (action.type) {
    case 'THEME_CHANGE':
      return {
        ...state,
        themeName: action.payload,
      };
    default:
      return state;
  }
};

export default function ThemeStateProvider({ forceTheme, children }: ThemeStateProviderProps) {
  const [state, dispatch] = useReducer(reducer, { themeName: initialState.themeName });

  const changeTheme = (themeName: ThemeName) => {
    setLocalItem(LS_KEYS.theme.key, themeName);
    dispatch({ type: 'THEME_CHANGE', payload: themeName });
  };

  const value: ThemeStateValue = {
    ...state,
    changeTheme,
  };

  useKeyboardShortcut(
    () => {
      changeTheme(state.themeName === 'light' ? 'dark' : 'light');
    },
    {
      key: 't',
      shiftKey: true,
      metaKey: true,
      ctrlKey: true,
    },
  );

  // used for storybook
  const currentTheme = forceTheme ?? state.themeName;

  return (
    <Theme
      scaling="100%"
      accentColor="orange"
      grayColor="gray"
      className={clsx('flex min-h-full leading-[normal]', currentTheme === 'dark' && 'dark')}
      appearance={currentTheme}
    >
      <ThemeState.Provider value={value}>
        <ThemeProvider theme={getTheme(currentTheme)}>
          <>
            <GlobalStyle />
            {children}
          </>
        </ThemeProvider>
      </ThemeState.Provider>
    </Theme>
  );
}
